import React, { useState, useEffect } from 'react';
import { RotateCcw, ChevronDown, ChevronRight, Film, ExternalLink } from 'lucide-react';
import { API_URL } from '../../config';

export default function VideoLogAdmin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [expandedPrompt, setExpandedPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState({});  // { [idx]: 'user' | 'bildanalyse' | 'drehbuch' | 'ltx' }
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('snapshot_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadVideoLog = async (newOffset = 0) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/video-log?limit=${limit}&offset=${newOffset}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setOffset(newOffset);
      }
    } catch (e) {
      console.error('Error loading video log:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideoLog(0);
  }, []);

  const getProviderBadge = (provider) => {
    if (!provider) return null;
    const p = provider.toLowerCase();
    if (p.includes('grok')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">Grok</span>;
    }
    if (p.includes('seedance')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Seedance</span>;
    }
    if (p.includes('pixverse')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">PixVerse</span>;
    }
    if (p.includes('kling')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">Kling</span>;
    }
    if (p.includes('veo')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">Veo</span>;
    }
    if (p.includes('ltx')) {
      return <span className="px-2 py-0.5 text-xs font-bold rounded bg-rose-500/20 text-rose-400 border border-rose-500/30">LTX</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-bold rounded bg-white/10 text-white/60 border border-white/20">{provider}</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') return <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">completed</span>;
    if (status === 'failed') return <span className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400">failed</span>;
    if (status === 'processing') return <span className="px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400">processing</span>;
    return <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">{status}</span>;
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Film className="w-7 h-7 text-purple-400" />
            Video Log
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Letzte 20 Video-Generierungen mit Provider, Prompt und Status.
          </p>
        </div>
        <button
          onClick={loadVideoLog}
          disabled={loading}
          className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition-all flex items-center gap-2"
        >
          <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Video Log Entries */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <button
          onClick={() => setOpen(!open)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-all"
        >
          <div className="flex items-center gap-3">
            <Film className="w-5 h-5 text-purple-400" />
            <span className="text-lg font-semibold text-white">Video-Generierungen</span>
            <span className="text-sm text-white/40">{total} Videos gesamt</span>
          </div>
          {open ? <ChevronDown className="w-5 h-5 text-white/40" /> : <ChevronRight className="w-5 h-5 text-white/40" />}
        </button>

        {open && (
          <div className="px-6 pb-6 space-y-3">
            {loading && entries.length === 0 ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
              </div>
            ) : entries.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-6">Keine Video-Eintraege gefunden.</p>
            ) : (
              <div className="space-y-2">
                {entries.map((entry, idx) => (
                  <div key={idx} className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-white/40 text-xs font-mono">
                        {entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '\u2014'}
                      </span>
                      {getProviderBadge(entry.provider)}
                      <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/50 border border-white/10 font-mono">
                        {entry.video_model}
                      </span>
                      {getStatusBadge(entry.status)}
                      {entry.cost_snaps > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">
                          {entry.cost_snaps} SNAPs
                        </span>
                      )}
                      {entry.cost_breakdown?.total_usd > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400 font-mono">
                          ${entry.cost_breakdown.total_usd.toFixed(4)}
                        </span>
                      )}
                      {entry.cost_breakdown?.duration_seconds > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/40">
                          {entry.cost_breakdown.duration_seconds}s
                        </span>
                      )}
                      <span className="text-white/20 text-xs ml-auto">User: {entry.user_id}</span>
                    </div>

                    {/* Pipeline-Tabs: User → Bildanalyse → Drehbuch → LTX Prompt */}
                    {(() => {
                      const tab = activeTab[idx] || 'user';
                      const tabs = [
                        { id: 'user', label: 'User-Prompt', color: 'emerald', content: entry.user_text },
                        { id: 'bildanalyse', label: 'Bildanalyst', color: 'blue', content: entry.agent_bildanalyse },
                        { id: 'drehbuch', label: 'Drehbuch', color: 'purple', content: entry.agent_drehbuch },
                        { id: 'ltx', label: 'LTX-Prompt', color: 'rose', content: entry.agent_ltx_prompt || entry.prompt },
                      ];
                      const current = tabs.find(t => t.id === tab) || tabs[0];
                      const isJson = current.content && typeof current.content === 'object';
                      const displayContent = isJson ? JSON.stringify(current.content, null, 2) : (current.content || '—');
                      const isExpanded = expandedPrompt === idx;

                      return (
                        <div className="space-y-1">
                          {/* Tab-Buttons */}
                          <div className="flex gap-1 flex-wrap">
                            {tabs.map(t => {
                              const hasContent = t.content && (typeof t.content === 'string' ? t.content.trim() : Object.keys(t.content).length > 0);
                              return (
                                <button
                                  key={t.id}
                                  onClick={() => setActiveTab(prev => ({ ...prev, [idx]: t.id }))}
                                  className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-t-lg transition-all ${
                                    tab === t.id
                                      ? `bg-${t.color}-500/20 text-${t.color}-400 border border-${t.color}-500/30 border-b-0`
                                      : hasContent
                                        ? 'bg-white/5 text-white/40 hover:text-white/60 border border-white/10 border-b-0'
                                        : 'bg-white/5 text-white/20 border border-white/5 border-b-0 opacity-50'
                                  }`}
                                  disabled={!hasContent}
                                >
                                  {t.label}
                                  {hasContent && <span className="ml-1 text-[8px]">●</span>}
                                </button>
                              );
                            })}
                          </div>
                          {/* Tab-Content */}
                          <div
                            className={`bg-black/30 border border-${current.color}-500/20 rounded-lg rounded-tl-none px-3 py-2 cursor-pointer`}
                            onClick={() => setExpandedPrompt(isExpanded ? null : idx)}
                          >
                            <pre className={`text-white/70 text-xs font-mono whitespace-pre-wrap break-words ${isExpanded ? '' : 'line-clamp-3'}`}>
                              {displayContent}
                            </pre>
                            {displayContent.length > 200 && !isExpanded && (
                              <span className="text-white/30 text-[10px]">Klicken zum Aufklappen...</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Error message */}
                    {entry.status === 'failed' && entry.error_message && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                        <p className="text-red-400 text-xs font-mono">{entry.error_message}</p>
                      </div>
                    )}

                    {/* Thumbnail + Video Link */}
                    <div className="flex items-center gap-3 mt-1">
                      {(entry.poster_thumbnail_url || entry.source_image_url) && (
                        <a href={entry.source_image_url || '#'} target="_blank" rel="noopener noreferrer">
                          <img
                            src={entry.poster_thumbnail_url || entry.source_image_url}
                            alt="source"
                            className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-white/30 transition-all"
                          />
                        </a>
                      )}
                      {entry.video_url && (
                        <a
                          href={entry.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30 rounded-lg transition-all"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Video ansehen
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-white/40 text-sm">
                  {offset + 1}–{Math.min(offset + limit, total)} von {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadVideoLog(Math.max(0, offset - limit))}
                    disabled={offset === 0 || loading}
                    className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg disabled:opacity-30 transition-all"
                  >
                    Zurück
                  </button>
                  <button
                    onClick={() => loadVideoLog(offset + limit)}
                    disabled={offset + limit >= total || loading}
                    className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg disabled:opacity-30 transition-all"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
